import { cloneDeep } from 'lodash-es';
import { TransferItem } from '@/models/transfer';
import { createTransferPageHeaders } from '@/pages/admin/transfer/TransferPageHeaders';
import { isRequestCanceled } from '@/services/baseService';
import { transferService } from '@/services/transferService';
import { createLoadingGenerationTracker } from '@/stores/composables/createLoadingGenerationTracker';
import { createStorePersistConfig } from '@/stores/composables/createStorePersistConfig';
import { useFilterPanelState } from '@/stores/composables/useFilterPanelState';
import { useFilterPersistence } from '@/stores/composables/useFilterPersistence';
import { IPageOptions } from '@/types';
import type { ITransferFilters } from '@/types/transfer';
import type { DataTableHeader } from '@/types/vuetify';

const defaultSortBy = [{ key: 'created_at', order: 'desc' as const }];

function createTransferStoreSetup(storeId: string) {
    // --- state ---
    const items = shallowRef<TransferItem[]>([]);
    const total = ref<number>(0);
    const loading = ref(false);
    const loadGeneration = createLoadingGenerationTracker();

    // --- filters ---
    const filters = reactive<ITransferFilters>({
        transferOperationId: {
            value: null,
            title: 'transfer.operationFilter',
            allowSaveState: true,
        },
        transferStatusId: {
            value: null,
            title: 'transfer.statusFilter',
            allowSaveState: true,
        },
        transferMethodId: {
            value: null,
            title: 'transfer.methodFilter',
            allowSaveState: true,
        },
        transferCurrencyId: {
            value: null,
            title: 'transfer.currencyFilter',
            allowSaveState: true,
        },
        transferUserId: {
            value: null,
            title: 'transfer.userFilter',
            allowSaveState: true,
        },
        transferApprovedUserId: {
            value: null,
            title: 'transfer.approvedByFilter',
            allowSaveState: true,
        },
    });

    // --- filter panel ---
    const { hiddenFilters, visibleFiltersMobile, filterOrder, availableFilters, useButtonForFiltersApply, saveFiltersState, visibleFilters } =
        useFilterPanelState(filters);

    // --- options ---
    const options = ref<IPageOptions>({
        itemsPerPage: 100,
        page: 1,
        rowsPerPageItems: [100, 200, 500],
        sortBy: [...defaultSortBy],
    });

    // --- reset ---
    function resetFilters() {
        filters.transferOperationId.value = null;
        filters.transferStatusId.value = null;
        filters.transferMethodId.value = null;
        filters.transferCurrencyId.value = null;
        filters.transferUserId.value = null;
        filters.transferApprovedUserId.value = null;
    }

    // --- table ---
    const headers = computed<DataTableHeader[]>(() => createTransferPageHeaders());

    const availableColumns = computed(() => headers.value.filter((header) => header.title));
    const hiddenColumns = ref<string[]>([]);
    const tableHeaders = computed<DataTableHeader[]>(() =>
        headers.value.filter((header) => header.key && (!hiddenColumns.value.includes(header.key) || !header.title) && !header.hidden)
    );

    // --- persistence ---
    const { reloadFiltersFromStorage } = useFilterPersistence({
        storeId,
        primaryStoreId: 'transfer',
        filters,
        saveFiltersState,
        resetFilters,
    });

    // --- actions ---
    async function loadData() {
        const loadId = loadGeneration.next();
        loading.value = true;
        try {
            const filtersToSend = cloneDeep(filters);

            const params = {
                filters: filtersToSend,
                options: {
                    sortBy: options.value.sortBy,
                    rowsPerPageItems: options.value.rowsPerPageItems,
                    page: options.value.page,
                    itemsPerPage: options.value.itemsPerPage,
                },
                requiredData: [],
                requiredDictionaries: ['NotPlayer', 'TransferMethod', 'AuditTable'],
            };

            const response = await transferService.list(params);

            items.value = response.data.rows.map((row) => new TransferItem(row));
            total.value = response.data.total;
        } catch (e) {
            if (isRequestCanceled(e)) return;
            throw e;
        } finally {
            if (loadGeneration.isLatest(loadId)) {
                loading.value = false;
            }
        }
    }

    function updateOptions(value: Partial<IPageOptions>) {
        if (value.page !== undefined && value.page !== options.value.page) {
            options.value.page = value.page;
            loadData();
        }
        if (value.itemsPerPage !== undefined && value.itemsPerPage !== options.value.itemsPerPage) {
            options.value.itemsPerPage = value.itemsPerPage;
            options.value.page = 1;
            loadData();
        }
    }

    function clearData() {
        items.value = [];
        total.value = 0;
        options.value.page = 1;
        loading.value = false;
    }

    async function deleteTransfer(id: number) {
        await transferService.delete(id);
        await loadData();
    }

    // --- return ---
    return {
        items,
        total,
        loading,
        filters,
        hiddenFilters,
        visibleFiltersMobile,
        filterOrder,
        availableFilters,
        useButtonForFiltersApply,
        saveFiltersState,
        visibleFilters,
        options,
        headers,
        availableColumns,
        hiddenColumns,
        tableHeaders,
        resetFilters,
        reloadFiltersFromStorage,
        loadData,
        updateOptions,
        clearData,
        deleteTransfer,
        hasAdd: true,
        hasRefresh: true,
    };
}

export function useTransferStore(storeId: string = 'transfer') {
    const useStore = defineStore(storeId, () => createTransferStoreSetup(storeId), {
        persist: createStorePersistConfig(storeId, 'transfer', defaultSortBy),
    });
    return useStore();
}

export type TransferStoreSetup = ReturnType<typeof useTransferStore>;
